import { createPinia } from 'pinia'
import { setupLayouts } from 'virtual:generated-layouts'
import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { routes } from 'vue-router/auto-routes'

import App from './App.vue'
import { getCachedSessionStorage } from './composables/useAppStorage'
import { useDesktopAvatarNotice } from './composables/useDesktopAvatarNotice'
import i18n from './locales'
import { useUserStore } from './stores/user'
import { logger } from './utils/logger'
import { createMobileAvatarGuard, isMobileAvatarClient } from './utils/mobile-avatar-guard'
import { initializePostHogClient, installAnalyticsClickTracking, installEarlyErrorCapture } from './utils/posthog-tracker'
import { clearInvitationBootstrap } from './utils/vendor-invitation-bootstrap'

import '@unocss/reset/normalize.css'
import '@unocss/reset/tailwind.css'
import 'vue-sonner/style.css'
import './style.css'
import './assets/index.css'
import './styles/studio.css'
import './styles/studio-dialogs.css'
import 'virtual:uno.css'

// First, so errors raised while the app boots are not lost before PostHog runs.
installEarlyErrorCapture()

// Consent is checked inside this function. Before it is granted the SDK is
// not initialized, so it cannot create identity cookies or load remote config.
initializePostHogClient()

const pinia = createPinia()
const userStore = useUserStore(pinia)

const router = createRouter({
  history: createWebHistory(),
  routes: setupLayouts(routes),
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition)
      return savedPosition
    if (to.hash)
      return { el: to.hash, top: 24, behavior: 'smooth' }
    return undefined
  },
})

router.afterEach((to) => {
  if (!/^\/invite\/?$/.test(to.path))
    clearInvitationBootstrap()
})

router.beforeEach((to) => {
  if (to.path === '/avatars/creations' || to.path === '/avatars/creations/') {
    return {
      path: '/stats',
      query: { ...to.query, tab: 'avatar-creations' },
      hash: to.hash,
      replace: true,
    }
  }

  if (to.path === '/avatars/personal') {
    return {
      path: '/avatars/custom',
      query: to.query,
      hash: to.hash,
    }
  }
})

router.beforeEach(createMobileAvatarGuard(
  isMobileAvatarClient,
  useDesktopAvatarNotice().show,
))

// Lazy route chunks that fail to load (e.g. a stale chunk after a deploy) land
// here instead of window.onerror. A tab opened before a deploy still points at
// the old chunk names, so load the target as a full page once to pick up the
// new build; the timestamp stops a loop when the chunk is genuinely broken.
// Written synchronously so it is stored before the page unloads.
const lastChunkReloadAt = getCachedSessionStorage('spatius:chunk-reload-at', 0, { flush: 'sync' })
router.onError((error, to) => {
  logger.error('Router error', error, { source: 'router' })
  const message = error instanceof Error ? error.message : ''
  if (!/dynamically imported module|module script failed/i.test(message))
    return
  if (Date.now() - lastChunkReloadAt.value < 10_000)
    return
  lastChunkReloadAt.value = Date.now()
  window.location.assign(to.fullPath)
})

void userStore.bootstrap()

const app = createApp(App)
// Vue swallows component errors, so they never reach the global handlers.
app.config.errorHandler = (error, _instance, info) => {
  logger.error('Vue error', error, { source: 'vue', vue_info: info })
}
app.use(router)
app.use(pinia)
app.use(i18n)

app.mount('#app')
installAnalyticsClickTracking()
